import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function LazyLoadingPage(): Node {
  return html`
    <div>
      <h1>Lazy Loading</h1>
      <p><code>lazy()</code> enables code splitting by loading components on demand. The module is only fetched when the component is first rendered.</p>

      <h2>Basic Usage</h2>
      ${CodeBlock(`import { lazy, html } from 'onefold';
import { Suspense } from 'onefold/suspense';

const HeavyChart = lazy(() => import('./components/HeavyChart'));

function Dashboard(): Node {
  return html\`
    <div>
      <h1>Dashboard</h1>
      \${Suspense(
        () => HeavyChart(),
        () => html\`<p>Loading chart...</p>\`
      )}
    </div>
  \`;
}`)}

      <h2>With Router</h2>
      <p>Combine <code>lazy()</code> with the router for route-level code splitting:</p>
      ${CodeBlock(`import { Router, lazy } from 'onefold';

const Home = lazy(() => import('./pages/Home'));
const Settings = lazy(() => import('./pages/Settings'));
const Analytics = lazy(() => import('./pages/Analytics'));

const App = Router([
  { path: '/', view: () => Home() },
  { path: '/settings', view: () => Settings() },
  { path: '/analytics', view: () => Analytics() },
]);`)}

      ${Callout('lazy() caches the module after the first load. Navigating back to a lazy-loaded route is instant.')}

      <h2>How It Works</h2>
      <ol>
        <li>On first render, <code>lazy()</code> calls the import function.</li>
        <li>The browser fetches the chunk over the network.</li>
        <li>The module's default export is called to produce a Node.</li>
        <li>The Node replaces the fallback in the DOM.</li>
        <li>Subsequent renders use the cached module — no network request.</li>
      </ol>

      <h2>Module Format</h2>
      <p>The lazily-loaded module must have a default export returning a Node:</p>
      ${CodeBlock(`// pages/Analytics.ts
import { html, createSignal } from 'onefold';

export default function Analytics(): Node {
  const period = createSignal('week');
  return html\`
    <div>
      <h2>Analytics</h2>
      <select onchange=\${(e: Event) => period.set((e.target as HTMLSelectElement).value)}>
        <option value="week">This Week</option>
        <option value="month">This Month</option>
      </select>
      <p>Showing: \${() => period()}</p>
    </div>
  \`;
}`)}

      <h2>Try It</h2>
      <p>Click "Load Heavy Component" — it simulates lazy loading with a delay:</p>

      ${Playground(`import { html, mount, createSignal, lazy } from 'onefold';

function App() {
  const showHeavy = createSignal(false);

  // lazy(loader, fallback) — loads the module on first call, shows fallback while loading
  const HeavyComponent = lazy(
    () => new Promise(resolve => {
      setTimeout(() => {
        resolve({
          default: () => html\`
            <div style="padding:20px;background:linear-gradient(135deg,#667eea,#764ba2);border-radius:8px;color:white">
              <h4>Heavy Component Loaded!</h4>
              <p style="font-size:13px;opacity:0.9;margin-top:8px">
                This simulates a code-split module that loads on demand.
                In a real app: lazy(() => import('./HeavyChart'))
              </p>
              <div style="margin-top:12px;display:flex;gap:8px">
                <div style="flex:1;height:40px;background:rgba(255,255,255,0.2);border-radius:4px"></div>
                <div style="flex:2;height:40px;background:rgba(255,255,255,0.15);border-radius:4px"></div>
                <div style="flex:1;height:40px;background:rgba(255,255,255,0.25);border-radius:4px"></div>
              </div>
            </div>
          \`
        });
      }, 2000);
    }),
    // Fallback shown while the module loads
    () => html\`
      <div style="padding:20px;text-align:center;background:#f8fafc;border:1px dashed #cbd5e1;border-radius:8px">
        <div style="width:24px;height:24px;border:3px solid #e2e8f0;border-top-color:#764ba2;border-radius:50%;animation:spin 0.8s linear infinite;margin:0 auto"></div>
        <p style="margin-top:8px;font-size:13px;color:#64748b">Loading component bundle...</p>
      </div>
    \`
  );

  return html\`
    <div>
      <h3>Lazy Loading — Code Splitting</h3>
      <p style="font-size:13px;color:#666;margin-bottom:12px">
        lazy(loader, fallback) defers loading until the component is rendered. Click the button to trigger it.
      </p>
      <button onclick=\${() => showHeavy.set(true)} style="margin-bottom:12px">
        \${() => showHeavy() ? 'Loaded!' : 'Load Heavy Component (2s delay)'}
      </button>
      \${() => showHeavy() ? HeavyComponent({}) : null}
      <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'lazy(loader, fallback) — Code Splitting')}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/performance/code-splitting">Code Splitting</a> — optimize bundle size with dynamic imports</li>
        <li><a href="/routing/router">Router</a> — route-level lazy loading integration</li>
      </ul>
    </div>
  `;
}
